import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/components/StatusBadge";
import PayoutProfileCard from "@/components/PayoutProfileCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Wallet as WalletIcon, ArrowDownToLine, ArrowUpFromLine, TrendingUp, Clock, Plus,
  HandCoins, ShoppingCart, Banknote, ArrowRight, Gift,
} from "lucide-react";
import PromoClaimCard from "@/components/PromoClaimCard";

// Payouts are Interac e-Transfer only. Withdrawals under $50 are admin-handled and a
// small Interac fee is passed on to the user; $50+ withdrawals are auto-paid in full.
const INTERAC_FEE = 0.5;
const MANUAL_THRESHOLD = 50;
const estFee = (amt) => {
  const a = parseFloat(amt) || 0;
  if (a <= 0 || a >= MANUAL_THRESHOLD) return 0;
  return Math.min(INTERAC_FEE, a);
};

const TXN_META = {
  topup: { label: "Top-up", Icon: Plus, cls: "text-blue-600" },
  payout_credit: { label: "Payout received", Icon: HandCoins, cls: "text-green-600" },
  fee_payment: { label: "Deal fee paid", Icon: ShoppingCart, cls: "text-foreground" },
  withdrawal: { label: "Withdrawal", Icon: ArrowUpFromLine, cls: "text-amber-600" },
};

const Stat = ({ Icon, label, value, accent }) => (
  <div className="rounded-xl border border-black/10 bg-white p-4">
    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
    <p className="mt-3 text-xs text-muted-foreground">{label}</p>
    <p className="font-heading text-xl" style={{ fontWeight: 700 }}>{money(value)}</p>
  </div>
);

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState(null);
  const [topupAmt, setTopupAmt] = useState("");
  const [wdAmt, setWdAmt] = useState("");
  const [topupOpen, setTopupOpen] = useState(false);
  const [wdOpen, setWdOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, t] = await Promise.all([api.get("/wallet"), api.get("/wallet/transactions")]);
    setSummary(s.data);
    setTxns(t.data);
    refresh();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topup = async () => {
    const amt = parseFloat(topupAmt);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    setBusy(true);
    try {
      await api.post("/wallet/topup", { amount: amt });
      toast.success(`Added ${money(amt)} to your wallet.`);
      setTopupOpen(false); setTopupAmt(""); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Top-up failed.");
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    const amt = parseFloat(wdAmt);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { amount: amt });
      toast.success(`Withdrawal of ${money(amt)} requested.`);
      setWdOpen(false); setWdAmt(""); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Withdrawal failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        <h1 className="font-heading text-4xl tracking-tight" style={{ fontWeight: 900 }}>Wallet</h1>
        <p className="mt-2 text-muted-foreground">Your BogoShare balance, earnings and payout history.</p>

        {summary === null ? (
          <Skeleton className="mt-8 h-40 rounded-2xl" />
        ) : (
          <>
            {/* Balance card */}
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
              data-testid="wallet-balance-card"
              className="mt-8 rounded-2xl border border-black/10 bg-foreground text-background p-6 sm:p-8"
            >
              <div className="flex items-center gap-2 text-background/70 text-sm">
                <WalletIcon className="h-4 w-4" /> Cash balance · withdrawable
              </div>
              <p data-testid="wallet-balance" className="mt-1 font-heading text-5xl tracking-tight" style={{ fontWeight: 900 }}>
                {money(summary.balance)}
              </p>
              {summary.promo_balance > 0 && (
                <div data-testid="promo-balance-chip" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-background/10 px-3 py-1.5 text-sm text-background">
                  <Gift className="h-3.5 w-3.5" />
                  <span className="font-semibold">{money(summary.promo_balance)}</span>
                  <span className="text-background/70">promo credit · fees only</span>
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="open-topup-btn" className="rounded-full bg-background text-foreground hover:bg-background/90">
                      <ArrowDownToLine className="mr-1.5 h-4 w-4" /> Add money
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add money to your wallet</DialogTitle>
                      <DialogDescription>Simulated top-up — no real card is charged in this demo.</DialogDescription>
                    </DialogHeader>
                    <div>
                      <Label htmlFor="topup-amount" className="text-xs">Amount (CAD)</Label>
                      <Input id="topup-amount" data-testid="topup-amount-input" type="number" min="1" value={topupAmt}
                        onChange={(e) => setTopupAmt(e.target.value)} placeholder="50.00" className="mt-1" />
                    </div>
                    <DialogFooter>
                      <Button data-testid="confirm-topup-btn" onClick={topup} disabled={busy} className="rounded-full">
                        {busy ? "Adding…" : "Add money"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={wdOpen} onOpenChange={setWdOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="open-withdraw-btn" variant="outline" className="rounded-full border-background/30 bg-transparent text-background hover:bg-background/10 hover:text-background">
                      <ArrowUpFromLine className="mr-1.5 h-4 w-4" /> Withdraw
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Withdraw funds</DialogTitle>
                      <DialogDescription>Cash out your wallet balance via Interac e-Transfer (simulated).</DialogDescription>
                    </DialogHeader>

                    <div>
                      <Label className="text-xs">Payout method</Label>
                      <div className="mt-1.5 rounded-lg border border-primary bg-primary/5 p-3 text-sm" data-testid="wd-method-interac">
                        <span className="font-semibold block">Interac e-Transfer</span>
                        <span className="text-[11px] text-muted-foreground">Sent to your Interac auto-deposit email. The only supported payout method.</span>
                      </div>
                    </div>

                    {!user?.payout_email ? (
                      <div data-testid="withdraw-needs-payout-email" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        Add your Interac auto-deposit email in the <b>Payout details</b> card below, then reopen this dialog to withdraw.
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground">Sent to <b>{user.payout_email}</b> via Interac auto-deposit.</p>
                        <div>
                          <Label htmlFor="wd-amount" className="text-xs">Amount (max {money(summary.balance)})</Label>
                          <Input id="wd-amount" data-testid="withdraw-amount-input" type="number" min="1" value={wdAmt}
                            onChange={(e) => setWdAmt(e.target.value)} placeholder="20.00" className="mt-1" />
                          {parseFloat(wdAmt) > 0 && parseFloat(wdAmt) < MANUAL_THRESHOLD && (
                            <div data-testid="withdraw-fee-preview" className="mt-2 flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2 text-xs">
                              <span className="text-muted-foreground">Est. Interac fee</span>
                              <span className="font-medium">
                                −{money(estFee(wdAmt))} · you receive ~{money(Math.max(0, (parseFloat(wdAmt) || 0) - estFee(wdAmt)))}
                              </span>
                            </div>
                          )}
                          {parseFloat(wdAmt) > 0 && parseFloat(wdAmt) < MANUAL_THRESHOLD ? (
                            <p data-testid="manual-payout-note" className="mt-1.5 text-[11px] text-amber-700">
                              Withdrawals under ${MANUAL_THRESHOLD} are processed manually by our team, and a small Interac fee is passed on to you.
                            </p>
                          ) : parseFloat(wdAmt) >= MANUAL_THRESHOLD && (
                            <p data-testid="auto-payout-note" className="mt-1.5 text-[11px] text-green-700">
                              Withdrawals of ${MANUAL_THRESHOLD}+ are auto-paid in full — you receive the entire amount.
                            </p>
                          )}
                        </div>
                        <DialogFooter>
                          <Button data-testid="confirm-withdraw-btn" onClick={withdraw} disabled={busy} className="rounded-full">
                            {busy ? "Requesting…" : "Withdraw"}
                          </Button>
                        </DialogFooter>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </motion.div>

            {/* Launch promo claim */}
            <div className="mt-6">
              <PromoClaimCard onClaimed={load} />
            </div>

            {/* Summary stats */}
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat Icon={TrendingUp} label="Total earned" value={summary.total_earned} accent="bg-green-100 text-green-700" />
              <Stat Icon={Clock} label="Pending earnings" value={summary.pending_earnings} accent="bg-amber-100 text-amber-700" />
              <Stat Icon={ShoppingCart} label="Total spent on fees" value={summary.total_spent} accent="bg-blue-100 text-blue-700" />
              <Stat Icon={Banknote} label="Total withdrawn" value={summary.total_withdrawn} accent="bg-secondary text-foreground" />
            </div>

            {/* Payout details */}
            <div className="mt-8">
              <PayoutProfileCard />
            </div>

            {/* Transactions */}
            <h2 className="mt-10 font-heading text-lg" style={{ fontWeight: 700 }}>Transaction history</h2>
            {txns === null ? (
              <Skeleton className="mt-4 h-40 rounded-xl" />
            ) : txns.length === 0 ? (
              <div className="mt-4 rounded-xl border border-black/10 bg-white p-8 text-center text-muted-foreground">
                No transactions yet. Add money or complete a split to get started.
              </div>
            ) : (
              <div className="mt-4 divide-y divide-black/5 rounded-xl border border-black/10 bg-white">
                {txns.map((t) => {
                  const meta = TXN_META[t.type] || { label: t.type, Icon: WalletIcon, cls: "text-foreground" };
                  const positive = t.amount >= 0;
                  return (
                    <div key={t.txn_id} data-testid={`txn-${t.txn_id}`} className="flex items-center gap-4 p-4">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-secondary ${meta.cls}`}>
                        <meta.Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{meta.label}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {t.description}
                          {t.reference ? ` · ${t.reference}` : ""}
                          {t.status === "pending" ? " · processing" : ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-heading text-base ${positive ? "text-green-600" : "text-foreground"}`} style={{ fontWeight: 700 }}>
                          {positive ? "+" : "−"}{money(Math.abs(t.amount))}
                        </p>
                        <p className="text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
